/**
 * Transform Dispatcher
 *
 * Routes to the appropriate layout strategy based on panel mode.
 * This is the main entry point for generating GridData from run data.
 */

import {getLayoutStrategy} from '../Layouts';
import {
  type ControlData,
  type GridData,
  type RowData,
  type SlotRegistry,
  type TransformInput,
} from '../types';

/**
 * Dispatches to the appropriate layout strategy based on panel mode.
 *
 * This is the main entry point for the layout system. It gets the
 * strategy from the registry and uses it to transform the data.
 *
 * @param input - Runs, step metadata, and configuration
 * @returns GridData structure ready for rendering
 */
export function dispatchTransform(input: TransformInput): GridData {
  const strategy = getLayoutStrategy(input.config);
  return strategy.transform(input);
}

/**
 * Builds precomputed per-row data for the given input.
 *
 * @param input - Runs, step metadata, and configuration
 * @param gridData - GridData produced by dispatchTransform() for the same input
 * @returns Array of RowData, one per visual row
 */
export function buildRowData(
  input: TransformInput,
  gridData: GridData
): RowData[] {
  const strategy = getLayoutStrategy(input.config);
  return strategy.buildRowData(input, gridData);
}

/**
 * Builds precomputed per-column control data for the given input.
 *
 * @param input - Runs, step metadata, and configuration
 * @param gridData - GridData produced by dispatchTransform() for the same input
 * @returns Array of ControlData, one per column
 */
export function buildControlData(
  input: TransformInput,
  gridData: GridData
): ControlData[] {
  const strategy = getLayoutStrategy(input.config);
  return strategy.buildControlData(input, gridData);
}

/**
 * Builds slot registry for the given configuration.
 *
 * @param config - Panel configuration
 * @param stepMeta - Optional step metadata (for checking if controls should be shown)
 * @returns Slot registry with control placement
 */
export function buildSlotRegistry(
  config: TransformInput['config'],
  stepMeta?: TransformInput['stepMeta'],
  isFullScreen?: boolean,
  isSingleRunView?: boolean
): SlotRegistry {
  const strategy = getLayoutStrategy(config);
  return strategy.buildSlotRegistry(
    config,
    stepMeta,
    isFullScreen,
    isSingleRunView
  );
}
